import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Compliance"` — unit P14-05.
 *
 * Recording, consent and data: what callers are told, how long things are kept, who at
 * WeTalk may open a recording, the do-not-call summary, requests about one person, and the
 * outbound caller ID.
 *
 * AMENDED 2026-09-23 (design fidelity, ADR 0018). The markup is the prototype's, element for
 * element: the three consent cards, the note and the shield-check disclosure, then the two
 * columns — "How long things are kept" on the left; Do not call, Requests about one person and
 * Outbound caller ID stacked on the right — with the prototype's own copy. What the earlier
 * render added beside it (a translation count under every consent line, two paragraphs about the
 * retention sweep, "Last changed …", the person-data field's hashing note, the caller ID as a
 * separate mono figure with a branding tag) is gone. Every behaviour stays:
 *
 *   · The operator-access switch sits at the end of the disclosure it governs, the sentence
 *     "you can turn staff access off … at any time" made operable; the sentence changes when it
 *     is off, so the disclosure never promises an access that has been withdrawn.
 *   · "Import a suppression list" opens the Do not call screen with its import panel already
 *     open (`#import`), and "View the list" opens the list: importing has one home, so it is
 *     audited in one place.
 *   · Person-data requests already made are listed under the form only when there are any.
 *
 * FOUR THINGS THIS SCREEN IS CAREFUL ABOUT, each of which a plainer render would get wrong:
 *
 * 1. **Refusal continues the conversation without recording.** The prototype's note says so:
 *    "the agent continues without recording and notes it on the call". A screen that implied
 *    the call ends would describe a product WeTalk does not sell.
 *
 * 2. **The sweep is ours.** Each retention select carries its note ("Deleted automatically."):
 *    AC-35 says the thing that makes that true is an application sweep on the outbox dispatcher,
 *    because a Blob lifecycle rule can delete on age and nothing else. The copy never says
 *    storage expires anything, because storage does not.
 *
 * 3. **A retention choice is picked from an offer set, never typed.** The four classes have
 *    different option sets on purpose, and {@link RETAIN_FOREVER_DAY} carries "keep until I
 *    delete them" as a value rather than as a missing one. A chosen day that is not in the
 *    offered set is rendered as the fault it is, using {@link retainDayLabel}, rather than
 *    silently snapping the control to the nearest option.
 *
 * 4. **AC-34's 24 hours is a deadline, and `overdue` is a breach.** `progress` already
 *    distinguishes "inside the window" from "late", so the screen never computes a `now` of
 *    its own — oxlint's `react(purity)` forbids `Date.now()` during render, and the honest
 *    clock here is the one the data was read at (`dataUpdatedAt`).
 *
 * The caller-ID card keeps registration and carrier-confirmed branding apart: the prototype's
 * sentence ("is registered … and branded as") is printed only when the carrier has confirmed
 * the branding; otherwise the same sentence says the branding is registered and not yet
 * confirmed.
 *
 * AMENDED 2026-09-26 (customer-portal review #210, closing #207). Every Compliance route is Owner
 * or Admin only. A Member or Viewer who opens `/compliance` directly (the sidebar does not offer
 * it to them) reads who manages it and their own role — `managedByText` — instead of the API's
 * raw refusal followed by "Nothing on this screen is guessed while it cannot be read…", which
 * read as an outage. `GET /v1/compliance` is not requested for them at all, so none of the
 * controls below can render for a role that could not use them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Dialog, Field, Icon, Select, Tag, Textarea } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { ComplianceColumns } from "./compliance-columns.js";
import { PersonDataRequestPanel } from "./person-data-request-panel.js";
import { useGateway } from "../../api/provider.js";
import {
  CONSENT_LINE_MAX_LENGTH,
  OPERATOR_ACCESS_NOTE,
  RETAIN_FOREVER_DAY,
  retainDayLabel,
  retentionClassLabel,
} from "../../api/governance-view.js";
import { apiErrorText } from "../../api/api-error.js";
import { QUERY_KEY, authSessionQuery, complianceQuery, languagePackQuery } from "../../api/query.js";
import { GLYPH } from "../../shell/glyph.js";
import { LanguageBetaTag, isBetaLanguage } from "../../shell/language-beta.js";
import { managedByText } from "../../shell/member-capability.js";
import type {
  ComplianceView,
  ConsentLineRowView,
  RecordingInForceView,
  OperatorAccessView,
  OutboundCallerIdView,
  RetentionSelectView,
  SpokenNoticeView,
} from "../../api/gateway.js";

import "./compliance.css";

/** The one place this screen turns an unknown rejection into a sentence. */
export function complianceErrorText(error: unknown): string {
  return apiErrorText(error, portalText("finance", "m0853"));
}

/**
 * How often the screen re-reads itself while a person-data request is still queued. The worker
 * usually finishes one in seconds; without a re-read the row would say "Due in 24 hours" until
 * the page is reloaded.
 */
const PENDING_REQUEST_POLL_MS = 5_000;

export function ComplianceScreen(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  // Every Compliance route is Owner or Admin only. Anyone else is told who manages it, and the
  // API is not asked at all (customer-portal review 2026-09-25 #210; the sidebar leaves the row
  // out for them too, #135).
  const authSession = useQuery(authSessionQuery(gateway));
  const signedIn = authSession.data ?? null;
  const withheld =
    signedIn === null
      ? null
      : managedByText(portalText("finance", "m0854"), "compliance.read", signedIn.memberRole, signedIn.accountName);
  const compliance = useQuery({
    ...complianceQuery(gateway),
    enabled: signedIn !== null && withheld === null,
    refetchInterval: (query) =>
      query.state.data?.personDataRequest.some((one) => one.progress.state !== "completed") === true
        ? PENDING_REQUEST_POLL_MS
        : false,
  });

  if (withheld !== null) {
    return (
      <section className="c-page cmp-page">
        <h1 className="cmp-title">{portalText("finance", "m0855")}</h1>
        <p className="c-note">{withheld}</p>
        <p className="c-empty-dek">{portalText("finance", "m0856")}</p>
      </section>
    );
  }

  if (compliance.isError) {
    return (
      <section className="c-page cmp-page">
        <h1 className="cmp-title">{portalText("finance", "m0855")}</h1>
        <p className="c-note c-note-critical">{complianceErrorText(compliance.error)}</p>
        <p className="c-empty-dek">{portalText("finance", "m0857")}</p>
      </section>
    );
  }

  if (compliance.data === undefined) {
    return (
      <section className="c-page cmp-page">
        <p className="c-note">{portalText("finance", "m0858")}</p>
      </section>
    );
  }

  return <ComplianceBody view={compliance.data} readAtMs={compliance.dataUpdatedAt} />;
}

function ComplianceBody({
  view,
  readAtMs,
}: {
  readonly view: ComplianceView;
  readonly readAtMs: number;
}): ReactNode {
  usePortalTranslation("finance");
  return (
    <section className="c-page cmp-page">
      <h1 className="cmp-title">{portalText("finance", "m0855")}</h1>
      <p className="cmp-lede">{portalText("finance", "m0859")}</p>

      <ConsentSection row={view.consentLine} recording={view.recording} />

      <OperatorAccessPanel access={view.operatorAccess} />

      <ComplianceColumns
        main={<RetentionPanel select={view.retention} />}
        side={
          <>
            <DoNotCallCard suppressedCount={view.suppressedCount} />
            <PersonDataRequestPanel
              row={view.personDataRequest}
              deadlineHour={view.personDataDeadlineHour}
              readAtMs={readAtMs}
            />
            <OutboundCallerIdCard callerId={view.outboundCallerId} />
          </>
        }
      />
    </section>
  );
}

// -----------------------------------------------------------------------------------------
// What callers are told
// -----------------------------------------------------------------------------------------

/**
 * What callers are told, and whether they are recorded — as the runtime actually behaves
 * (customer-portal review #66). The runtime speaks the language pack's recording notice, then
 * records, on every call of a live agent whose version records; the old red "no consent line is
 * configured, so nothing is recorded" was false on both counts and offered no way to clear it.
 * The notice is shown per language, with which live agents record and which do not. Custom
 * `consent_line` rows, when a workspace has any, still show.
 *
 * AMENDED 2026-09-26 (customer-portal review #204): each language's notice can be edited. It reads
 * "your wording" or "WeTalk's standard wording"; Edit opens {@link ConsentLineDialog}, and a save
 * reaches callers at once (the API republishes the agents that speak it). A voice row of the
 * per-medium block is not repeated once the workspace's own wording shows above it.
 */
function ConsentSection({
  row,
  recording,
}: {
  readonly row: readonly ConsentLineRowView[];
  readonly recording: RecordingInForceView | null;
}): ReactNode {
  usePortalTranslation("finance");
  const ownWordingShown = recording?.spokenNotice.some((one) => one.source === "workspace") === true;
  const shownRow = ownWordingShown ? row.filter((one) => one.medium !== "voice") : row;
  return (
    <>
      <p className="cmp-section-title">{portalText("finance", "m0860")}</p>

      {recording === null ? null : <RecordingInForce recording={recording} />}

      {shownRow.length === 0 ? null : (
        <div className="cmp-consent">
          {shownRow.map((one) => (
            <div
              key={one.consentLineId}
              className="cmp-consent-row"
              title={portalText("finance", "consent.translated", { count: one.languageCount, medium: mediumText(one.medium) })}
            >
              <span className="cmp-consent-body">
                <span className="cmp-consent-where">{one.surfaceLabel}</span>
                <span className="cmp-consent-line">&ldquo;{one.text}&rdquo;</span>
              </span>
              <Tag tone={one.enabled && one.medium === "voice" ? "accent" : "neutral"}>
                {one.stateLabel}
              </Tag>
            </div>
          ))}
        </div>
      )}

      <p className="cmp-note">{portalText("finance", "m0862")}</p>
    </>
  );
}

/** `["A", "B", "C"]` → "A, B and C". */
function namesText(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return portalText("finance", "m0733", { value1: names.slice(0, -1).join(", "), value2: names.at(-1) ?? "" });
}

function RecordingInForce({ recording }: { readonly recording: RecordingInForceView }): ReactNode {
  usePortalTranslation("finance");
  const recorded = recording.recordingVoiceAgent;
  const unrecorded = recording.notRecordingVoiceAgent;
  const [editing, setEditing] = useState<SpokenNoticeView | null>(null);
  const [saved, setSaved] = usePortalMessage();
  // plan-026 (0176): a beta language's notice carries its Beta badge, from the catalogue's maturity.
  const languagePack = useQuery(languagePackQuery(useGateway()));
  if (recorded.length === 0 && unrecorded.length === 0) {
    return (
      <p className="cmp-note">{portalText("finance", "m0863")}</p>
    );
  }
  return (
    <>
      <p className="cmp-note">
        {recorded.length === 0
          ? portalText("finance", "m0864")
          : portalText("finance", "m0865", { value1: namesText(recorded) })}
        {unrecorded.length === 0 ? "" : portalText("finance", "consent.notRecorded", { count: unrecorded.length, names: namesText(unrecorded) })}
      </p>
      {recording.spokenNotice.length === 0 ? null : (
        <div className="cmp-consent">
          {recording.spokenNotice.map((one) => (
            <div key={one.languageCode} className="cmp-consent-row">
              <span className="cmp-consent-body">
                <span className="cmp-consent-where">
                  {portalText("finance", "m0867")}{one.languageName}
                  {isBetaLanguage(languagePack.data, one.languageCode) ? (
                    <>
                      {" "}
                      <LanguageBetaTag />
                    </>
                  ) : null}
                </span>
                <span className="cmp-consent-line">&ldquo;{one.text}&rdquo;</span>
                <span className="cmp-consent-source">
                  {one.source === "workspace" ? portalText("finance", "m0868") : portalText("finance", "m0869")}
                </span>
                {one.outOfDateVoiceAgent.length === 0 ? null : (
                  <span className="cmp-consent-stale">
                    {portalText("finance", "consent.stale", { count: one.outOfDateVoiceAgent.length, names: namesText(one.outOfDateVoiceAgent) })}</span>
                )}
              </span>
              <span className="cmp-consent-actions">
                <Tag tone="accent">{portalText("finance", "m0873")}</Tag>
                <Button
                  variant="secondary"
                  aria-label={portalText("finance", "m0874", { value1: one.languageName })}
                  onClick={() => {
                    setSaved(null);
                    setEditing(one);
                  }}
                >{portalText("finance", "m0875")}</Button>
              </span>
            </div>
          ))}
        </div>
      )}

      {saved === null ? null : (
        <p className="cmp-note" role="status">
          {saved}
        </p>
      )}

      {editing === null ? null : (
        <ConsentLineDialog
          key={editing.languageCode}
          notice={editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={(text) => {
            setEditing(null);
            setSaved(text);
          }}
        />
      )}
    </>
  );
}

/** "Saved. Roma orders now says it." — who a save reached, in words. */
export function consentLineSavedText(standard: boolean, republished: readonly string[]): string {
  const what = standard ? portalText("finance", "m0876") : portalText("finance", "m0877");
  if (republished.length === 0) return portalText("finance", "m0878", { value1: what });
  return portalText("finance", "consent.saved", { count: republished.length, what, names: namesText(republished) });
}

/**
 * Editing one language's notice (review #204). The API checks the line — not empty, at most 300
 * characters, and none of the language's "don't record me" phrases — and says why when it refuses;
 * the dialog shows that sentence and keeps the draft. There is no "off": the standard line comes back
 * instead, and a recording is never started without telling the caller.
 */
function ConsentLineDialog({
  notice,
  onClose,
  onSaved,
}: {
  readonly notice: SpokenNoticeView;
  readonly onClose: () => void;
  readonly onSaved: (text: () => string) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(notice.text);
  const [message, setMessage] = usePortalMessage();
  const length = [...draft.trim()].length;

  const save = useMutation({
    mutationFn: (text: string | null) =>
      text === null
        ? gateway.restoreStandardConsentLine(notice.languageCode)
        : gateway.saveConsentLine(notice.languageCode, text),
    onSuccess: (result, text) => {
      queryClient.setQueryData(QUERY_KEY.compliance, result.compliance);
      onSaved(() => consentLineSavedText(text === null, result.republishedVoiceAgent));
    },
    onError: (error: unknown) => {
      setMessage(() => (complianceErrorText(error)));
    },
  });

  return (
    <Dialog
      open
      title={portalText("finance", "m0880", { value1: notice.languageName })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("finance", "m0418")}</Button>
          {notice.source === "workspace" && notice.standardText !== null ? (
            <Button
              variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                save.mutate(null);
              }}
            >{portalText("finance", "m0881")}</Button>
          ) : null}
          <Button
            variant="primary"
            disabled={save.isPending || length === 0 || length > CONSENT_LINE_MAX_LENGTH}
            onClick={() => {
              setMessage(null);
              save.mutate(draft);
            }}
          >
            {save.isPending ? portalText("finance", "m0484") : portalText("finance", "m0882")}
          </Button>
        </>
      }
    >
      <Field
        label={portalText("finance", "m0883")}
        description={portalText("finance", "m0884", { value1: String(length), value2: String(CONSENT_LINE_MAX_LENGTH) })}
        {...(message === null ? {} : { error: message })}
      >
        {(control) => (
          <Textarea
            {...control}
            value={draft}
            rows={3}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
              setMessage(null);
            }}
          />
        )}
      </Field>
      {notice.standardText === null ? null : (
        <p className="cmp-note">{portalText("finance", "m0885")}{notice.standardText}&rdquo;</p>
      )}
      <p className="cmp-note">{portalText("finance", "m0886")}{notice.languageName}{portalText("finance", "m0887")}</p>
    </Dialog>
  );
}

/** The enum's own copy. The surface label says where; this says over what. */
function mediumText(medium: ConsentLineRowView["medium"]): string {
  if (medium === "voice") return portalText("finance", "m0888");
  if (medium === "web_chat") return portalText("finance", "m0889");
  if (medium === "whatsapp") return portalText("finance", "m0890");
  return portalText("finance", "m0891");
}

// -----------------------------------------------------------------------------------------
// Who at WeTalk may open a recording
// -----------------------------------------------------------------------------------------

function OperatorAccessPanel({ access }: { readonly access: OperatorAccessView }): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [message, setMessage] = usePortalMessage();

  const change = useMutation({
    mutationFn: (enabled: boolean) => gateway.setOperatorAccess(enabled),
    onSuccess: async () => {
      setMessage(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.compliance });
    },
    onError: (error: unknown) => {
      setMessage(() => (complianceErrorText(error)));
    },
  });

  const switchLabel = access.enabled
    ? portalText("finance", "m0892")
    : portalText("finance", "m0893");

  return (
    <>
      <div className="cmp-disclosure">
        <span className="cmp-disclosure-glyph">
          <Icon glyph={GLYPH.shieldCheck} size="xl" tone="accent" />
        </span>
        <span className="cmp-disclosure-line">{portalText("finance", "m0894")}{" "}
          {access.enabled
            ? OPERATOR_ACCESS_NOTE()
            : portalText("finance", "m0895")}
          {/* The promise is only worth reading if the customer can see it kept. */}
          {access.lastPlayedByOperatorText === null
            ? null
            : portalText("finance", "m0896", { value1: access.lastPlayedByOperatorText })}
        </span>
        <label
          className="cmp-switch"
          title={portalText("finance", "m0897", { value1: switchLabel, value2: access.changedText, value3: access.changedByName === null ? "" : portalText("finance", "compliance.changedBy", { name: access.changedByName }) })}
        >
          <input
            type="checkbox"
            role="switch"
            checked={access.enabled}
            disabled={change.isPending}
            onChange={(event) => {
              change.mutate(event.currentTarget.checked);
            }}
          />
          <span className="cmp-switch-track" aria-hidden="true">
            <span className="cmp-switch-knob" />
          </span>
          <span className="c-visually-hidden">{switchLabel}</span>
        </label>
      </div>
      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}
    </>
  );
}

// -----------------------------------------------------------------------------------------
// How long things are kept
// -----------------------------------------------------------------------------------------

/**
 * The classes one select writes. The prototype draws a single "Orders and survey answers" select,
 * and the API keeps the two as separate classes with the same offer, so that select writes both;
 * otherwise survey answers would keep a period nobody on this screen chose.
 */
function savedClassOf(retentionClass: string): readonly string[] {
  return retentionClass === "order" ? ["order", "survey_result"] : [retentionClass];
}

/** Longer is larger; "keep until I delete them" is the longest of all. */
function retainRank(retainDay: number): number {
  return retainDay === RETAIN_FOREVER_DAY ? Number.POSITIVE_INFINITY : retainDay;
}

type RetentionChange = {
  readonly retentionClass: string;
  readonly retainDay: number;
};

/** A shortening waiting for its confirmation, with what the sweep would remove (the preview). */
type PendingShortening = RetentionChange & { readonly affectedCount: number };

function counted(count: number, one: string, many: string): string {
  switch (one) {
    case "past conversation": return portalText("finance", "retention.past_conversation", { count: count });
    case "conversation": return portalText("finance", "retention.conversation", { count: count });
    case "call recording": return portalText("finance", "retention.call_recording", { count: count });
    default: return `${String(count)} ${count === 1 ? one : many}`;
  }
}

/**
 * What the confirmation says a shortening will do, with the preview's count. Only swept classes
 * get here — recordings, transcripts, caller details — because the sweep keeps orders and survey
 * answers whatever their period (customer-portal review item 203).
 */
export function shorteningText(change: PendingShortening): string {
  const period = retainDayLabel(change.retainDay).toLowerCase();
  const n = change.affectedCount;
  const after = portalText("finance", "m0898");
  if (change.retentionClass === "caller_detail") {
    return change.retainDay === 0
      ? portalText("finance", "m0899", { value1: counted(n, "past conversation", "past conversations"), value2: after })
      : portalText("finance", "m0900", { value1: counted(n, "conversation", "conversations"), value2: period, value3: after });
  }
  if (change.retentionClass === "transcript") {
    return portalText("finance", "m0901", { value1: counted(n, "conversation", "conversations"), value2: period, value3: after });
  }
  return portalText("finance", "m0902", { value1: counted(n, "call recording", "call recordings"), value2: period, value3: after });
}

function RetentionPanel({
  select,
}: {
  readonly select: readonly RetentionSelectView[];
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [message, setMessage] = usePortalMessage();
  // A shortening waits here until it is confirmed: the next sweep deletes what it no longer keeps.
  const [shortening, setShortening] = useState<PendingShortening | null>(null);

  const save = useMutation({
    mutationFn: async (next: RetentionChange) => {
      for (const retentionClass of savedClassOf(next.retentionClass)) {
        await gateway.saveRetentionPolicy(retentionClass, next.retainDay);
      }
    },
    onSuccess: async () => {
      setMessage(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.compliance });
    },
    onError: (error: unknown) => {
      setMessage(() => (complianceErrorText(error)));
    },
  });

  // A shortening asks the API what the next sweep would remove before anything is saved.
  const preview = useMutation({
    mutationFn: (next: RetentionChange) => gateway.previewRetentionPolicy(next.retentionClass, next.retainDay),
    onSuccess: (found, next) => {
      // A class the sweep keeps loses nothing to a shorter period, so there is nothing to confirm.
      if (!found.swept) {
        save.mutate(next);
        return;
      }
      setShortening({ ...next, affectedCount: found.affectedCount });
    },
    onError: (error: unknown) => {
      setMessage(() => (complianceErrorText(error)));
    },
  });

  const choose = (one: RetentionSelectView, retainDay: number): void => {
    const next = { retentionClass: one.class, retainDay };
    if (retainRank(retainDay) < retainRank(one.chosenRetainDay)) {
      preview.mutate(next);
      return;
    }
    save.mutate(next);
  };

  return (
    <div className="cmp-card">
      <p className="cmp-card-kicker cmp-card-kicker-wide">{portalText("finance", "m0903")}</p>

      <div className="cmp-retention">
        {select.map((one) => {
          const offered = one.option.some((option) => option.retainDay === one.chosenRetainDay);
          return (
            <div key={one.class} className="cmp-retention-item">
              <Field label={retentionClassLabel(one.class)} description={one.note}>
                {(control) => (
                  <Select
                    {...control}
                    value={String(one.chosenRetainDay)}
                    disabled={save.isPending || preview.isPending}
                    onChange={(event) => {
                      choose(one, Number(event.currentTarget.value));
                    }}
                  >
                    {offered ? null : (
                      <option value={String(one.chosenRetainDay)}>
                        {retainDayLabel(one.chosenRetainDay)}
                      </option>
                    )}
                    {one.option.map((option) => (
                      <option key={option.retainDay} value={String(option.retainDay)}>
                        {option.retainDay === one.defaultRetainDay ? portalText("finance", "m0904", { value1: option.label }) : option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {one.chosenRetainDay === RETAIN_FOREVER_DAY ? (
                <p className="cmp-note-tight">{portalText("finance", "m0905")}</p>
              ) : null}

              {offered ? null : (
                <p className="c-note c-note-critical">{portalText("finance", "m0906")}{retainDayLabel(one.chosenRetainDay)}{portalText("finance", "m0907")}</p>
              )}
            </div>
          );
        })}
      </div>

      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}

      <Dialog
        open={shortening !== null}
        title={portalText("finance", "m0908")}
        onClose={() => {
          setShortening(null);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShortening(null);
              }}
            >{portalText("finance", "m0418")}</Button>
            <Button
              variant="primary"
              disabled={save.isPending}
              onClick={() => {
                if (shortening !== null) {
                  save.mutate({ retentionClass: shortening.retentionClass, retainDay: shortening.retainDay });
                }
                setShortening(null);
              }}
            >
              {shortening === null
                ? portalText("finance", "m0909")
                : shortening.retainDay === 0
                  ? portalText("finance", "m0910")
                  : portalText("finance", "m0911", { value1: retainDayLabel(shortening.retainDay) })}
            </Button>
          </>
        }
      >
        {shortening === null ? null : (
          <p className="c-note">{shorteningText(shortening)}</p>
        )}
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------------------------------
// Do not call
// -----------------------------------------------------------------------------------------

function DoNotCallCard({ suppressedCount }: { readonly suppressedCount: number }): ReactNode {
  usePortalTranslation("finance");
  return (
    <div className="cmp-card">
      <div className="cmp-card-head">
        <p className="cmp-card-kicker">{portalText("finance", "m0912")}</p>
        <Tag tone="neutral">
          {String(suppressedCount)} {suppressedCount === 1 ? "number" : "numbers"}
        </Tag>
      </div>
      <p className="cmp-card-body">{portalText("finance", "m0913")}</p>
      <div className="cmp-card-actions">
        {/* Importing has one home — the Do not call screen — so it is audited in one place.
            This opens that screen with its import panel already open. */}
        <Link
          to="/do-not-call"
          search={{ filter: "all", q: "" }}
          hash="import"
          className="btn btn-secondary"
        >{portalText("finance", "m0914")}</Link>
        <Link
          to="/do-not-call"
          search={{ filter: "all", q: "" }}
          className="btn btn-ghost cmp-inline-button"
        >{portalText("finance", "m0915")}</Link>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------------------
// Outbound caller ID
// -----------------------------------------------------------------------------------------

function OutboundCallerIdCard({
  callerId,
}: {
  readonly callerId: OutboundCallerIdView | null;
}): ReactNode {
  usePortalTranslation("finance");
  if (callerId === null) {
    return (
      <div className="cmp-card">
        <p className="cmp-card-kicker cmp-card-kicker-wide">{portalText("finance", "m0916")}</p>
        <p className="cmp-card-body cmp-card-body-flush">{portalText("finance", "m0917")}</p>
      </div>
    );
  }

  return (
    <div className="cmp-caller-id">
      <p className="cmp-caller-id-title">{portalText("finance", "m0916")}</p>
      <p className="cmp-caller-id-body">
        {callerId.brandingVerified
          ? portalText("finance", "m0918", { value1: callerId.e164, value2: callerId.brandedName })
          : portalText("finance", "m0919", { value1: callerId.e164, value2: callerId.brandedName })}{" "}
        {callerId.note}
      </p>
    </div>
  );
}
